import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, Typography, Box, Stack, CircularProgress } from '@mui/material';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import CallEndOutlinedIcon from '@mui/icons-material/CallEndOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import { callConfig } from '../configs/call';
import { useUser } from '../hooks/useUser';
import { fetchUserByRole } from '../services/users';
import { useCallSession } from './useCallSession';

const CallManagerContext = createContext(null);

const SIGNALING_RETRY_DELAYS_MS = [1000, 2000, 5000, 10000, 15000];
const INITIAL_SIGNALING_SERVICE_STATUS = Object.freeze({
    phase: 'initializing',
    message: ''
});

const dialogPaperSx = {
    borderRadius: '22px',
    boxShadow: '0px 10px 30px rgba(61, 61, 61, 0.12)'
};

const dialogTitleSx = {
    px: 3,
    pt: 3,
    pb: 1.5,
    fontWeight: 700,
    color: '#3D3D3D'
};

const secondaryButtonSx = {
    minHeight: 46,
    px: 2.2,
    textTransform: 'none',
    fontWeight: 700,
    color: '#3D3D3D',
    borderColor: '#D9D9D9',
    borderWidth: '2px',
    borderRadius: '16px',
    '&:hover': {
        bgcolor: '#F7F7F7',
        borderColor: '#CFCFCF',
        borderWidth: '2px'
    }
};

const primaryButtonSx = {
    minHeight: 46,
    px: 2.4,
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: '16px',
    bgcolor: '#3D3D3D',
    boxShadow: 'none',
    '&:hover': {
        bgcolor: '#2F2F2F',
        boxShadow: 'none'
    }
};

export const CallManagerProvider = ({ children, onSignalingStatusChange }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useUser();

    const isLocalDevMode = callConfig.mode === 'local-dev';
    const effectiveUserId = isLocalDevMode ? callConfig.devUserId : user.uuid;

    const [bootstrapError, setBootstrapError] = useState(null);
    const bootstrapRef = useRef(false);
    const outgoingCancelledByUserRef = useRef(false);
    const hadOutgoingRingingRef = useRef(false);
    const [outgoingFeedback, setOutgoingFeedback] = useState(null);
    const dismissedResumeCallIdRef = useRef(null);
    const incomingRingAudioRef = useRef(null);
    const incomingRingUnlockedRef = useRef(false);
    const [incomingRingBlocked, setIncomingRingBlocked] = useState(false);
    const [incomingRemoteUserName, setIncomingRemoteUserName] = useState('');
    const [acceptingIncomingCall, setAcceptingIncomingCall] = useState(false);
    const [signalingServiceStatus, setSignalingServiceStatus] = useState(
        INITIAL_SIGNALING_SERVICE_STATUS
    );
    const initialSignalingAttemptFinishedRef = useRef(false);
    const signalingRecoveryInFlightRef = useRef(false);
    const signalingRetryTimerRef = useRef(null);
    const signalingRetryAttemptRef = useRef(0);
    const signalingSuspendedRef = useRef(false);
    const signalingRecoveryGenerationRef = useRef(0);
    const signalingRecoveryContextRef = useRef(null);
    const scheduleSignalingRecoveryRef = useRef(null);
    const signalingRecoveryPendingRef = useRef(false);

    const callSession = useCallSession({
        enabled: callConfig.featureEnabled,
        userRoleUuid: user.selectedRole,
        userId: effectiveUserId
    });

    const { state, uiState, actions } = callSession;
    const activeRemoteUserId = state?.activeCall?.remoteUserId || null;
    const isOutgoingRinging = Boolean(uiState?.isOutgoingRinging && activeRemoteUserId);
    const canConnectSignaling = Boolean(
        callConfig.featureEnabled && (isLocalDevMode || user.selectedRole)
    );

    signalingRecoveryContextRef.current = {
        actions,
        activeCallId: state?.activeCall?.callId || null,
        canConnect: canConnectSignaling,
        signalingState: state?.signalingConnectionState
    };

    const updateSignalingServiceStatus = useCallback((phase, message = '') => {
        setSignalingServiceStatus((currentStatus) => {
            if (currentStatus.phase === phase && currentStatus.message === message) {
                return currentStatus;
            }
            return { phase, message };
        });
    }, []);

    const clearSignalingRetryTimer = useCallback(() => {
        if (signalingRetryTimerRef.current) {
            clearTimeout(signalingRetryTimerRef.current);
            signalingRetryTimerRef.current = null;
        }
    }, []);

    const runSignalingRecovery = useCallback(async () => {
        const context = signalingRecoveryContextRef.current;
        if (
            !context?.canConnect ||
            signalingSuspendedRef.current ||
            !window.navigator.onLine ||
            signalingRecoveryInFlightRef.current
        ) {
            return;
        }

        const generation = signalingRecoveryGenerationRef.current;
        const recoveryToken = {};
        signalingRecoveryInFlightRef.current = recoveryToken;
        updateSignalingServiceStatus(
            'recovering',
            'A restabelecer a ligação ao serviço de chamadas...'
        );

        let shouldScheduleAnotherAttempt = false;
        try {
            const result = await context.actions.reconnectSignaling({
                tryResume: Boolean(context.activeCallId)
            });

            if (generation !== signalingRecoveryGenerationRef.current) {
                return;
            }

            if (result?.ok) {
                signalingRetryAttemptRef.current = 0;
                setBootstrapError(null);
                updateSignalingServiceStatus('connected');
            } else {
                signalingRetryAttemptRef.current += 1;
                updateSignalingServiceStatus(
                    'unavailable',
                    'Ligação às chamadas indisponível. A tentar restabelecer automaticamente...'
                );
                shouldScheduleAnotherAttempt = true;
            }
        } finally {
            if (signalingRecoveryInFlightRef.current === recoveryToken) {
                signalingRecoveryInFlightRef.current = null;
            }
            if (signalingRecoveryPendingRef.current) {
                signalingRecoveryPendingRef.current = false;
                scheduleSignalingRecoveryRef.current?.({ immediate: true, force: true });
            } else if (shouldScheduleAnotherAttempt) {
                scheduleSignalingRecoveryRef.current?.({ force: true });
            }
        }
    }, [updateSignalingServiceStatus]);

    const scheduleSignalingRecovery = useCallback(
        ({ immediate = false, force = false, queueIfBusy = false } = {}) => {
            const context = signalingRecoveryContextRef.current;
            if (immediate && signalingRetryTimerRef.current) {
                clearTimeout(signalingRetryTimerRef.current);
                signalingRetryTimerRef.current = null;
            }

            if (signalingRecoveryInFlightRef.current) {
                if (queueIfBusy) {
                    signalingRecoveryPendingRef.current = true;
                }
                return;
            }

            if (
                !context?.canConnect ||
                signalingSuspendedRef.current ||
                !window.navigator.onLine ||
                signalingRetryTimerRef.current ||
                (!force &&
                    (context.signalingState === 'AUTHENTICATED' ||
                        context.signalingState === 'READY'))
            ) {
                return;
            }

            const retryIndex = Math.max(0, signalingRetryAttemptRef.current - 1);
            const delay = immediate
                ? 0
                : SIGNALING_RETRY_DELAYS_MS[
                      Math.min(retryIndex, SIGNALING_RETRY_DELAYS_MS.length - 1)
                  ];

            signalingRetryTimerRef.current = setTimeout(() => {
                signalingRetryTimerRef.current = null;
                void runSignalingRecovery();
            }, delay);
        },
        [runSignalingRecovery]
    );

    scheduleSignalingRecoveryRef.current = scheduleSignalingRecovery;

    const retrySignalingConnection = useCallback(() => {
        clearSignalingRetryTimer();
        signalingRetryAttemptRef.current = 0;
        scheduleSignalingRecovery({ immediate: true, force: true, queueIfBusy: true });
    }, [clearSignalingRetryTimer, scheduleSignalingRecovery]);

    useEffect(() => {
        bootstrapRef.current = false;
        initialSignalingAttemptFinishedRef.current = false;
        signalingRecoveryGenerationRef.current += 1;
        signalingRecoveryInFlightRef.current = null;
        signalingRecoveryPendingRef.current = false;
        signalingRetryAttemptRef.current = 0;
        signalingSuspendedRef.current = false;
        clearSignalingRetryTimer();
        updateSignalingServiceStatus('initializing');
        setBootstrapError(null);
    }, [clearSignalingRetryTimer, effectiveUserId, updateSignalingServiceStatus, user.selectedRole]);

    useEffect(() => {
        if (
            !callConfig.featureEnabled ||
            (!isLocalDevMode && !user.selectedRole) ||
            bootstrapRef.current
        ) {
            return;
        }

        bootstrapRef.current = true;

        const bootstrap = async () => {
            setBootstrapError(null);

            const connectResult = await actions.connectSignaling();
            if (!connectResult.ok) {
                setBootstrapError(connectResult.error);
                initialSignalingAttemptFinishedRef.current = true;
                updateSignalingServiceStatus(
                    'unavailable',
                    'Ligação às chamadas indisponível. A tentar restabelecer automaticamente...'
                );
                signalingRetryAttemptRef.current = 1;
                scheduleSignalingRecovery({ force: true });
                return;
            }

            initialSignalingAttemptFinishedRef.current = true;
            signalingRetryAttemptRef.current = 0;
            updateSignalingServiceStatus('connected');
            await actions.refreshDevices();
        };

        void bootstrap();
    }, [actions, isLocalDevMode, scheduleSignalingRecovery, updateSignalingServiceStatus, user.selectedRole]);

    useEffect(() => {
        if (
            state?.signalingConnectionState === 'AUTHENTICATED' ||
            state?.signalingConnectionState === 'READY'
        ) {
            setBootstrapError(null);
            signalingRetryAttemptRef.current = 0;
            clearSignalingRetryTimer();
            updateSignalingServiceStatus('connected');
        }
    }, [clearSignalingRetryTimer, state?.signalingConnectionState, updateSignalingServiceStatus]);

    useEffect(() => {
        const signalingState = state?.signalingConnectionState;
        if (
            !initialSignalingAttemptFinishedRef.current ||
            signalingSuspendedRef.current ||
            !window.navigator.onLine ||
            !['DISCONNECTED', 'ERROR', 'RECONNECTING'].includes(signalingState)
        ) {
            return;
        }

        updateSignalingServiceStatus(
            'unavailable',
            'Ligação às chamadas indisponível. A tentar restabelecer automaticamente...'
        );
        scheduleSignalingRecovery({
            immediate: signalingRetryAttemptRef.current === 0
        });
    }, [scheduleSignalingRecovery, state?.signalingConnectionState, updateSignalingServiceStatus]);

    useEffect(() => {
        const handlePageHide = (event) => {
            if (!event.persisted) return;

            signalingSuspendedRef.current = true;
            signalingRecoveryGenerationRef.current += 1;
            signalingRecoveryPendingRef.current = false;
            clearSignalingRetryTimer();
            updateSignalingServiceStatus('suspended');
            void signalingRecoveryContextRef.current?.actions?.disconnectSignaling();
        };

        const handlePageShow = (event) => {
            if (!event.persisted) return;

            signalingSuspendedRef.current = false;
            scheduleSignalingRecovery({
                immediate: true,
                force: true,
                queueIfBusy: true
            });
        };

        const handleOffline = () => {
            signalingRecoveryGenerationRef.current += 1;
            signalingRecoveryPendingRef.current = false;
            clearSignalingRetryTimer();
            updateSignalingServiceStatus('offline');
            void signalingRecoveryContextRef.current?.actions?.disconnectSignaling();
        };

        const handleOnline = () => {
            scheduleSignalingRecovery({
                immediate: true,
                force: true,
                queueIfBusy: true
            });
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState !== 'visible') return;

            const signalingState = signalingRecoveryContextRef.current?.signalingState;
            if (['DISCONNECTED', 'ERROR', 'RECONNECTING'].includes(signalingState)) {
                scheduleSignalingRecovery({ immediate: true });
            }
        };

        window.addEventListener('pagehide', handlePageHide);
        window.addEventListener('pageshow', handlePageShow);
        window.addEventListener('offline', handleOffline);
        window.addEventListener('online', handleOnline);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('pagehide', handlePageHide);
            window.removeEventListener('pageshow', handlePageShow);
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('online', handleOnline);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [clearSignalingRetryTimer, scheduleSignalingRecovery, updateSignalingServiceStatus]);

    useEffect(() => {
        onSignalingStatusChange?.({
            ...signalingServiceStatus,
            retry: retrySignalingConnection
        });
    }, [onSignalingStatusChange, retrySignalingConnection, signalingServiceStatus]);

    useEffect(() => {
        return () => {
            signalingRecoveryGenerationRef.current += 1;
            clearSignalingRetryTimer();
        };
    }, [clearSignalingRetryTimer]);

    const incomingRemoteUserId = state?.activeCall?.remoteUserId || null;
    const hasIncomingCall = Boolean(uiState?.isIncomingRinging && incomingRemoteUserId);
    const activeCallId = state?.activeCall?.callId || null;
    const hasResumableCallState = Boolean(
        activeCallId &&
            (uiState?.isInCall || uiState?.isNegotiating || uiState?.isReconnecting)
    );
    const hasResumableRemoteUser = Boolean(state?.activeCall?.remoteUserId);
    const isOnCallRoute = location.pathname.startsWith('/call/');
    const shouldShowResumeDialog = Boolean(
        callConfig.resumeEnabled &&
        hasResumableCallState &&
            hasResumableRemoteUser &&
            !isOnCallRoute &&
            !uiState?.isIncomingRinging &&
            !uiState?.isOutgoingRinging &&
            dismissedResumeCallIdRef.current !== activeCallId
    );

    useEffect(() => {
        if (!activeCallId) {
            dismissedResumeCallIdRef.current = null;
        }
    }, [activeCallId]);

    useEffect(() => {
        if (!hasIncomingCall || !incomingRemoteUserId || !user?.selectedRole) {
            setIncomingRemoteUserName('');
            setAcceptingIncomingCall(false);
            return;
        }

        let cancelled = false;

        const loadIncomingRemoteUserName = async () => {
            try {
                const response = await fetchUserByRole({
                    userRoleUuid: user.selectedRole,
                    roleUuid: incomingRemoteUserId
                });

                if (!cancelled) {
                    setIncomingRemoteUserName(
                        response?.data?.name ||
                        response?.data?.displayName ||
                        ''
                    );
                }
            } catch {
                if (!cancelled) {
                    setIncomingRemoteUserName('');
                }
            }
        };

        void loadIncomingRemoteUserName();

        return () => {
            cancelled = true;
        };
    }, [hasIncomingCall, incomingRemoteUserId, user?.selectedRole]);

    useEffect(() => {
        if (!incomingRingAudioRef.current) {
            const audio = new Audio('/assets/sounds/incoming-call.mp3');
            audio.loop = true;
            audio.preload = 'auto';
            audio.volume = 0.22;
            incomingRingAudioRef.current = audio;
        }

        const audio = incomingRingAudioRef.current;

        if (hasIncomingCall) {
            audio.currentTime = 0;
            const playPromise = audio.play();
            if (playPromise?.catch) {
                playPromise
                    .then(() => {
                        incomingRingUnlockedRef.current = true;
                        setIncomingRingBlocked(false);
                    })
                    .catch(() => {
                        setIncomingRingBlocked(true);
                    });
            }
        } else {
            audio.pause();
            audio.currentTime = 0;
            setIncomingRingBlocked(false);
        }

        return () => {
            audio.pause();
            audio.currentTime = 0;
        };
    }, [hasIncomingCall]);

    useEffect(() => {
        const audio = incomingRingAudioRef.current;
        if (!audio) {
            return undefined;
        }

        const primeOrRetryRing = () => {
            if (!incomingRingUnlockedRef.current) {
                const previousVolume = audio.volume;
                audio.volume = 0;
                const unlockPromise = audio.play();
                if (unlockPromise?.catch) {
                    unlockPromise
                        .then(() => {
                            audio.pause();
                            audio.currentTime = 0;
                            audio.volume = previousVolume;
                            incomingRingUnlockedRef.current = true;

                            if (hasIncomingCall) {
                                const retryPromise = audio.play();
                                if (retryPromise?.catch) {
                                    retryPromise
                                        .then(() => setIncomingRingBlocked(false))
                                        .catch(() => setIncomingRingBlocked(true));
                                }
                            }
                        })
                        .catch(() => {
                            audio.volume = previousVolume;
                        });
                }
                return;
            }

            if (hasIncomingCall) {
                const retryPromise = audio.play();
                if (retryPromise?.catch) {
                    retryPromise
                        .then(() => setIncomingRingBlocked(false))
                        .catch(() => setIncomingRingBlocked(true));
                }
            }
        };

        window.addEventListener('pointerdown', primeOrRetryRing);
        window.addEventListener('keydown', primeOrRetryRing);

        return () => {
            window.removeEventListener('pointerdown', primeOrRetryRing);
            window.removeEventListener('keydown', primeOrRetryRing);
        };
    }, [hasIncomingCall]);

    useEffect(() => {
        if (isOutgoingRinging) {
            hadOutgoingRingingRef.current = true;
            setOutgoingFeedback(null);
            return;
        }

        if (!hadOutgoingRingingRef.current) {
            return;
        }

        if (uiState?.isInCall) {
            hadOutgoingRingingRef.current = false;
            outgoingCancelledByUserRef.current = false;
            return;
        }

        if (outgoingCancelledByUserRef.current) {
            setOutgoingFeedback({
                severity: 'info',
                message: 'Call canceled.'
            });
            outgoingCancelledByUserRef.current = false;
            hadOutgoingRingingRef.current = false;
            return;
        }

        if (uiState?.status === 'idle' && state?.activeCall === null) {
            const endedReason = (uiState?.lastEndedCall?.reason || '').toUpperCase();
            setOutgoingFeedback({
                severity: 'warning',
                message:
                    endedReason === 'REJECTED'
                        ? 'O destinatário rejeitou a chamada.'
                        : 'A chamada terminou antes de ser atendida.'
            });
            hadOutgoingRingingRef.current = false;
        }
    }, [
        isOutgoingRinging,
        state?.activeCall,
        uiState?.isInCall,
        uiState?.lastEndedCall?.reason,
        uiState?.status
    ]);

    const handleAcceptIncoming = async () => {
        if (acceptingIncomingCall) {
            return;
        }

        setAcceptingIncomingCall(true);
        setBootstrapError(null);

        const mediaResult = await actions.acquireLocalMedia({
            audio: true,
            video: true
        });

        if (!mediaResult.ok) {
            const fallbackAudioOnlyResult = await actions.acquireLocalMedia({
                audio: true,
                video: false
            });

            if (!fallbackAudioOnlyResult.ok) {
                setBootstrapError(mediaResult.error);
                setAcceptingIncomingCall(false);
                return;
            }
        }

        const acceptResult = await actions.acceptCall();
        if (!acceptResult.ok) {
            setBootstrapError(acceptResult.error);
            setAcceptingIncomingCall(false);
            return;
        }

        if (incomingRemoteUserId) {
            navigate(`/call/${incomingRemoteUserId}`);
        }
    };

    const handleRejectIncoming = async () => {
        if (acceptingIncomingCall) {
            return;
        }

        const rejectResult = await actions.rejectCall();
        if (!rejectResult.ok) {
            setBootstrapError(rejectResult.error);
            return;
        }
    };

    const handleCancelOutgoing = useCallback(async () => {
        outgoingCancelledByUserRef.current = true;
        const endResult = await actions.endCall();
        if (!endResult.ok) {
            setBootstrapError(endResult.error);
        }
    }, [actions]);

    const dismissOutgoingFeedback = useCallback(() => {
        setOutgoingFeedback(null);
    }, []);

    const handleDismissResumeDialog = useCallback(() => {
        if (activeCallId) {
            dismissedResumeCallIdRef.current = activeCallId;
        }
    }, [activeCallId]);

    const handleReturnToCall = useCallback(() => {
        const remoteUserId = state?.activeCall?.remoteUserId;
        if (!remoteUserId) return;

        navigate(`/call/${remoteUserId}`);
    }, [navigate, state?.activeCall?.remoteUserId]);

    const value = useMemo(
        () => ({
            ...callSession,
            bootstrapError,
            isBootstrapped: bootstrapRef.current,
            outgoingCallUi: {
                isRinging: isOutgoingRinging,
                targetUserId: activeRemoteUserId
            },
            outgoingFeedback,
            signalingServiceStatus,
            retrySignalingConnection,
            cancelOutgoingCall: handleCancelOutgoing,
            dismissOutgoingFeedback
        }),
        [
            activeRemoteUserId,
            bootstrapError,
            callSession,
            dismissOutgoingFeedback,
            handleCancelOutgoing,
            isOutgoingRinging,
            outgoingFeedback,
            retrySignalingConnection,
            signalingServiceStatus
        ]
    );

    const dialogError = bootstrapError || callSession.errorMessage || null;

    return (
        <CallManagerContext.Provider value={value}>
            {children}

            <Dialog
                open={hasIncomingCall}
                onClose={acceptingIncomingCall ? undefined : handleRejectIncoming}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle sx={dialogTitleSx}>Chamada recebida</DialogTitle>
                <DialogContent sx={{ px: 3, pb: 1.5, pt: 0.5 }}>
                    <Stack spacing={2.25} alignItems="center" textAlign="center">
                        <Box
                            sx={{
                                width: 78,
                                height: 78,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'rgba(214, 55, 49, 0.08)',
                                color: '#D63731',
                                border: '1px solid rgba(214, 55, 49, 0.18)'
                            }}
                        >
                            <CallOutlinedIcon sx={{ fontSize: 34 }} />
                        </Box>
                        <Stack spacing={0.75} alignItems="center">
                            <Typography variant="h6" fontWeight={700} color="#171717">
                                {incomingRemoteUserName || incomingRemoteUserId || 'Chamada a entrar'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320 }}>
                                Está a receber uma videochamada. Pode aceitar agora ou rejeitar.
                            </Typography>
                        </Stack>
                        {incomingRingBlocked ? (
                            <Typography variant="body2" color="warning.main">
                                Clique na página para ativar o som da chamada.
                            </Typography>
                        ) : null}
                        {dialogError ? (
                            <Typography variant="body2" color="error">
                                {dialogError}
                            </Typography>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'center', gap: 1.25 }}>
                    <Button
                        variant="outlined"
                        onClick={handleRejectIncoming}
                        startIcon={<CallEndOutlinedIcon />}
                        sx={secondaryButtonSx}
                        disabled={acceptingIncomingCall}
                    >
                        Rejeitar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAcceptIncoming}
                        startIcon={
                            acceptingIncomingCall ? (
                                <CircularProgress size={18} color="inherit" />
                            ) : (
                                <CallOutlinedIcon />
                            )
                        }
                        sx={primaryButtonSx}
                        disabled={acceptingIncomingCall}
                    >
                        {acceptingIncomingCall ? 'A atender...' : 'Atender'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={shouldShowResumeDialog}
                onClose={handleDismissResumeDialog}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Active Call Detected</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        {state?.activeCall?.remoteUserId
                            ? `You still have an active call with ${state.activeCall.remoteUserId}.`
                            : 'You still have an active call.'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Status: {uiState?.status}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDismissResumeDialog}>Later</Button>
                    <Button variant="contained" onClick={handleReturnToCall}>
                        Return To Call
                    </Button>
                </DialogActions>
            </Dialog>
        </CallManagerContext.Provider>
    );
};

export const useCallManager = () => {
    const context = useContext(CallManagerContext);
    if (!context) {
        throw new Error('useCallManager must be used within CallManagerProvider');
    }
    return context;
};
